import { Link } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import { siteIntro, topicList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

const team = [
  { name: '内容编辑部', role: '资讯 · 选片', desc: '每天整理影视动态，负责首页焦点与近期推荐。' },
  { name: '专题策划组', role: '专题 · 片单', desc: '按主题打包作品，把零散的片单整理成可读的线索。' },
  { name: '产品与技术', role: '体验 · 数据', desc: '维护分类与筛选体系，让查找作品这件事更短一点。' },
];

const faqs = [
  { q: '菠萝蜜网可以免费看电影吗？', a: '不可以。本站是影视资讯与推荐平台，不提供在线播放、下载或任何形式的片源服务。' },
  { q: '作品评分是怎么来的？', a: '当前页面中的评分、热度与剧集信息均为演示数据，用于展示页面功能，不代表真实评价。' },
  { q: '如何浏览某个题材的作品？', a: '在顶部导航选择电影或电视剧分类，用“类型 / 地区 / 年份”筛选即可，也可直接在搜索框输入题材关键词。' },
  { q: '收藏的内容会同步到其他设备吗？', a: '不会。收藏仅保存在当前浏览器本地，清除浏览器数据后会丢失。' },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 菠萝蜜网',
    description: '了解菠萝蜜网的定位、内容范围与联系方式：我们专注影视资讯整理与作品推荐，不提供在线播放与下载服务。',
    keywords: '菠萝蜜网,关于我们,影视资讯平台,影视推荐网站',
    path: '/about',
  });

  return (
    <SiteLayout>
      <PortalShell showRail={false}>
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="relative overflow-hidden rounded-xl bg-secondary-950">
          <div className="relative h-[220px] w-full md:h-[280px]">
            <img
              src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20still%20of%20an%20empty%20small%20neighborhood%20cinema%20at%20dusk%2C%20warm%20amber%20marquee%20light%2C%20deep%20green%20shadows%2C%20quiet%20nostalgic%20atmosphere%2C%20soft%20film%20grain%2C%20no%20text&width=1600&height=600&seq=boluomi-about-hero&orientation=landscape"
              alt="菠萝蜜网品牌头图"
              title="关于菠萝蜜网"
              className="h-full w-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/70 to-transparent" />
          </div>
          <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-10">
            <p className="text-[11px] tracking-[0.22em] text-accent-400">ABOUT BOLUOMI</p>
            <h1 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-4xl">关于菠萝蜜网</h1>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-background-200/80 md:text-sm">{siteIntro.slogan}</p>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-5">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">我们是谁</h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-700">{siteIntro.description}</p>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-700">
                我们的工作方式很朴素：先把作品资料一条条核对清楚，再按类型、地区、年份归类，最后用专题把相关的作品串起来。你可能不会在这里停留很久，但希望每次停留都能带走一部想看的片子。
              </p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">内容范围</h2>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { icon: 'ri-film-line', title: '电影与剧集资讯', desc: '档期、定档、预告与口碑动态，每日更新。' },
                  { icon: 'ri-layout-grid-line', title: '分类与筛选体系', desc: '电影、剧集、动漫、综艺、纪录片五大分类。' },
                  { icon: 'ri-bookmark-3-line', title: '专题片单', desc: '按主题整理的作品集合，附编辑说明。' },
                  { icon: 'ri-heart-3-line', title: '个人收藏（本地）', desc: '收藏保存在浏览器本地，无需登录。' },
                ].map((item) => (
                  <div key={item.title} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <h3 className="mt-2 text-sm font-medium text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">团队</h2>
              <div className="mt-3 space-y-3">
                {team.map((member) => (
                  <div key={member.name} className="flex flex-col gap-1 border-l-2 border-primary-300 pl-3 sm:flex-row sm:items-baseline sm:gap-3">
                    <p className="text-sm font-medium text-foreground-900">{member.name}</p>
                    <p className="text-[11px] text-primary-700">{member.role}</p>
                    <p className="text-[11px] text-foreground-500 sm:ml-auto">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">常见问题</h2>
              <div className="mt-3 divide-y divide-background-100">
                {faqs.map((faq) => (
                  <div key={faq.q} className="py-3 first:pt-0">
                    <h3 className="text-sm font-medium text-foreground-900">{faq.q}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="space-y-5">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">联系我们</h2>
              <div className="mt-3 space-y-3">
                <a href="mailto:hello@boluomi.film" className="flex items-center gap-3 rounded-md border border-background-200 px-3 py-2.5 transition hover:border-primary-400 hover:bg-primary-50/40">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-400">邮箱</span>
                    <span className="block truncate text-sm text-foreground-900">hello@boluomi.film</span>
                  </span>
                </a>
                <a href="mailto:cooperation@boluomi.film" className="flex items-center gap-3 rounded-md border border-background-200 px-3 py-2.5 transition hover:border-primary-400 hover:bg-primary-50/40">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-briefcase-line text-base" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-400">商务合作</span>
                    <span className="block truncate text-sm text-foreground-900">cooperation@boluomi.film</span>
                  </span>
                </a>
              </div>
              <div className="mt-4">
                <p className="text-[11px] text-foreground-400">社交媒体</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { icon: 'ri-wechat-line', label: '微信公众号' },
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-douban-line', label: '豆瓣' },
                    { icon: 'ri-bilibili-line', label: '哔哩哔哩' },
                  ].map((social) => (
                    <span key={social.label} className="flex items-center gap-1.5 rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-700">
                      <i className={`${social.icon} w-3.5 h-3.5 flex items-center justify-center`} />
                      {social.label}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">使用说明</h2>
              <ol className="mt-3 space-y-2 text-xs leading-relaxed text-foreground-600">
                <li>1. 通过顶部导航或左侧栏目进入分类页。</li>
                <li>2. 用类型、地区、年份缩小范围，再选择排序方式。</li>
                <li>3. 点击作品卡片进入详情页，可查看简介与剧集章节。</li>
                <li>4. 在详情页点击“收藏”，即可在收藏页查看。</li>
              </ol>
            </section>

            <section id="disclaimer" className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="font-heading text-base font-bold text-accent-900">免责声明</h2>
              <p className="mt-2 text-[11px] leading-relaxed text-accent-900/80">
                本站所有影视作品名称、简介、评分、剧集信息及图片均为演示内容，仅用于展示产品功能，与真实作品资料无关。本站不提供在线播放、下载或任何片源服务。
              </p>
              <p id="privacy" className="mt-3 border-t border-accent-200/70 pt-3 text-[11px] leading-relaxed text-accent-900/80">
                隐私说明：收藏数据仅保存在您的浏览器本地，我们不会上传或收集任何个人信息。清除浏览器数据后收藏记录将丢失。
              </p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">热门专题</h2>
              <div className="mt-3 space-y-2">
                {topicList.map((topic) => (
                  <Link key={topic.id} to={`/topic/${topic.id}`} className="flex items-center justify-between rounded-md bg-background-100/70 px-3 py-2 transition hover:bg-primary-50">
                    <span className="text-xs text-foreground-800">{topic.title}</span>
                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400" />
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </PortalShell>
    </SiteLayout>
  );
}